import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Collapse } from 'src/components/transitions/Collapse';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import * as Yup from 'yup';

interface TwoFactorPromptProps {
  userId: number;
  method: 'email' | 'app';
  message: string;
  onVerify: (otpCode: string) => Promise<void>;
  onResend?: () => Promise<void>;
  isVerifying?: boolean;
  isResending?: boolean;
  error?: string;
  successMessage?: string;
}

export const TwoFactorPrompt: React.FC<TwoFactorPromptProps> = ({
  method,
  message,
  onVerify,
  onResend,
  isVerifying = false,
  isResending = false,
  error,
  successMessage,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'twoFactor' });
  const [localError, setLocalError] = useState('');

  const formik = {
    initialValues: {
      otpCode: '',
    },
    validationSchema: Yup.object({
      otpCode: Yup.string()
        .required(t('code-required') || 'Two-factor code is required')
        .matches(/^\d{6}$/, t('code-format') || 'Code must be 6 digits')
        .length(6, t('code-length') || 'Code must be exactly 6 digits'),
    }),
    onSubmit: async (values) => {
      try {
        setLocalError('');
        await onVerify(values.otpCode);
      } catch (e) {
        const error = e as any;
        setLocalError(
          error?.response?.data?.message ||
            t('verification-error') ||
            'Verification failed. Please try again.'
        );
      }
    },
  };

  const handleResend = async () => {
    if (onResend) {
      try {
        setLocalError('');
        await onResend();
      } catch (e) {
        const error = e as any;
        setLocalError(
          error?.response?.data?.message ||
            t('resend-error') ||
            'Failed to resend code. Please try again.'
        );
      }
    }
  };

  const displayError = error || localError;

  return (
    <Card
      sx={{
        p: 3,
        backgroundColor: 'transparent',
      }}
    >
      <Stack display="flex" justifyContent="center" alignItems="center">
        <Typography variant="h4" mb={1} align="center">
          {t('title') || 'Two-Factor Authentication Required'}
        </Typography>
      </Stack>
      <Collapse in={!!displayError} collapsedSize={0}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {displayError}
        </Alert>
      </Collapse>
      <Collapse in={!!successMessage} collapsedSize={0}>
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      </Collapse>
      <Typography variant="body1" mb={3} align="center" color="text.secondary">
        {message}
      </Typography>
      <Formik enableReinitialize={true} {...formik}>
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <FormTextField
              label={t('code-label') || 'One-Time Password'}
              name="otpCode"
              placeholder={t('code-placeholder') || 'Enter 6-digit code'}
              InputProps={{
                inputProps: {
                  maxLength: 6,
                  pattern: '[0-9]*',
                  inputMode: 'numeric' as const,
                  autoComplete: 'one-time-code',
                },
              }}
              autoFocus
              sx={{ mt: 0 }}
            />

            <Button
              variant="prominent"
              sx={{ mt: 3 }}
              disabled={isSubmitting || isVerifying || !isValid || !dirty}
              loading={isSubmitting || isVerifying}
              type="submit"
              fullWidth
            >
              {t('verify-button') || 'Verify'}
            </Button>

            {method === 'email' && onResend && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="borderless"
                  size="small"
                  onClick={handleResend}
                  disabled={isResending || isSubmitting || isVerifying}
                  loading={isResending}
                >
                  {t('resend-button') || 'Resend Email'}
                </Button>
              </Box>
            )}
          </Form>
        )}
      </Formik>
      <Typography
        variant="body2"
        sx={{ mt: 3 }}
        align="center"
        color="text.secondary"
      >
        {method === 'email'
          ? t('email-help') || 'Check your email for the verification code.'
          : t('app-help') ||
            'Open your authenticator app to get the verification code.'}
      </Typography>
    </Card>
  );
};
